// Pure aggregation: zet ruwe Firestore-data om in het signals-object dat
// de score-helpers verwachten. Hier geen Firestore-imports.

import { businessDaysBetween } from '../../utils/businessDays';
import { formGroupKey } from '../groupResponsesIntoForms';

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH_APPROX = 30 * DAY;

const REPORT_RESEARCH_THRESHOLD = 5;     // ≥5 ingevulde formulieren in 12mo → "rapport-waardig"
const ACTIVE_USER_WINDOW_MS = 30 * DAY;  // proxy: 30 cal days ≈ ~22wd, voldoende voor v1

const safeToDate = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (ts instanceof Date) return ts;
    return null;
};

const buildSignals = ({ entity = {}, entityEvents = [], entityResearches = [], entityTasks = [], entityUsers = [], now = new Date() }) => {
    const nowMs = now.getTime();

    // lastLoginBusinessDaysAgo
    const lastLoginDate = safeToDate(entity.LastLogin);
    const lastLoginBusinessDaysAgo = lastLoginDate ? businessDaysBetween(lastLoginDate, now) : null;

    // activeUserCountLast30wd
    let activeUserCountLast30wd = 0;
    for (const user of entityUsers) {
        const lastLogin = safeToDate(user.LastLogin);
        if (lastLogin && nowMs - lastLogin.getTime() <= ACTIVE_USER_WINDOW_MS) {
            activeUserCountLast30wd += 1;
        }
    }

    // events: last 8w vs the 8w before that, plus most-recent date in the fetched window
    let eventsLast8w = 0;
    let eventsLast8wPrevPeriod = 0;
    let eventsCountInWindow = 0;
    let lastEventMs = null;
    const eightWeeksAgoMs = nowMs - 8 * WEEK;
    const sixteenWeeksAgoMs = nowMs - 16 * WEEK;
    for (const ev of entityEvents) {
        const evDate = safeToDate(ev.Timestamp);
        if (!evDate) continue;
        const t = evDate.getTime();
        if (t > nowMs) continue;
        if (t > sixteenWeeksAgoMs) {
            eventsCountInWindow += 1;
            if (lastEventMs === null || t > lastEventMs) lastEventMs = t;
        }
        if (t > eightWeeksAgoMs) eventsLast8w += 1;
        else if (t > sixteenWeeksAgoMs) eventsLast8wPrevPeriod += 1;
    }

    // researches in last 12mo, plus most-recent date in window.
    // entityResearches zijn QuestionnairesResponses: één document PER ANTWOORD,
    // dus een vragenlijst van 17 stellingen levert er 17 op. Tellen we die
    // ruw, dan meldt de kolom 'Onderzoeken' 357 voor twaalf invullers en gaat
    // hasReportLast12mo al om bij één invuller die vijf vragen beantwoordde.
    // groupResponsesIntoForms is de gedeelde groepering (FormID, met de
    // milliseconde als terugval voor oude antwoorden zonder FormID).
    // AI-CONTEXT: See docs/pitfalls.md#database-totaldocs-overcounts-respondents
    // Deze lus draait over ELK opgehaald antwoorddocument van de tenant, dus
    // hier geen kopieën of tussenobjecten: alleen de sleutels tellen en het
    // maximum bijhouden. lastResearchMs is bewust het NIEUWSTE antwoord en niet
    // de datum van het nieuwste formulier — een formulier draagt de datum van
    // zijn vroegste antwoord, en die kan dagen eerder liggen.
    const twelveMonthsAgoMs = nowMs - 12 * MONTH_APPROX;
    const formulieren = new Set();
    let lastResearchMs = null;
    for (const r of entityResearches) {
        const d = safeToDate(r.Timestamp);
        if (d === null) continue;
        const t = d.getTime();
        if (t <= twelveMonthsAgoMs) continue;
        formulieren.add(formGroupKey(r.FormID || '', t));
        if (lastResearchMs === null || t > lastResearchMs) lastResearchMs = t;
    }
    const researchesLast12mo = formulieren.size;
    const researchesCountInWindow = formulieren.size;

    // tasks planned in next 30 days (calendar approx for 30wd — tolerantie OK voor v1)
    let tasksPlannedNext30wd = 0;
    const thirtyDaysFromNowMs = nowMs + 30 * DAY;
    for (const t of entityTasks) {
        const d = safeToDate(t.Timestamp);
        if (!d) continue;
        const tMs = d.getTime();
        if (tMs > nowMs && tMs <= thirtyDaysFromNowMs) tasksPlannedNext30wd += 1;
    }

    // hasReportLast12mo proxy: minstens N onderzoeksresponses in 12mo
    const hasReportLast12mo = researchesLast12mo >= REPORT_RESEARCH_THRESHOLD;

    // daysSinceCreated
    const createdDate = safeToDate(entity.Timestamp);
    const daysSinceCreated = createdDate
        ? businessDaysBetween(createdDate, now)
        : Number.MAX_SAFE_INTEGER;

    return {
        lastLoginBusinessDaysAgo,
        activeUserCountLast30wd,
        eventsLast8w,
        eventsLast8wPrevPeriod,
        eventsCountInWindow,
        lastEventDate: lastEventMs ? new Date(lastEventMs) : null,
        researchesLast12mo,
        researchesCountInWindow,
        lastResearchDate: lastResearchMs ? new Date(lastResearchMs) : null,
        tasksPlannedNext30wd,
        hasReportLast12mo,
        daysSinceCreated,
    };
};

export default buildSignals;
